// Work-packages view (wireframe 09) — phase-grouped tree on the left,
// per-task detail (header + properties + dependencies) in the middle.
// Right pin pane carries the "Impact of moving this task" what-if
// affordance.

import { useMemo, useState } from "react";
import { useShell, useRightPane } from "../components/ShellContext.js";
import { useTool } from "../lib/useTool.js";
import { ErrorState, Skeleton } from "./common.js";

interface TaskRow {
  id: string;
  phase: string;
  name: string;
  duration_days: number | null;
  depends?: string[];
  resources?: string[];
  is_critical?: boolean;
}

interface DependencyNode {
  id: string;
  label: string;
  is_critical?: boolean;
}

export default function WorkPackagesView() {
  const { project, openChat } = useShell();
  const tasksTool = useTool("list_tasks", { project });
  const wbsTool = useTool("render_wbs", { project });
  const depTool = useTool("render_dependency_network", { project });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const tasks = ((tasksTool.env?.data as { rows?: TaskRow[] } | undefined)?.rows ?? []) as TaskRow[];
  const wbsData = (wbsTool.env?.data as { phases?: Array<{ id: string; code: string | null; name: string }> } | undefined);
  const phases = wbsData?.phases ?? [];
  const depData = (depTool.env?.data as { edges?: Array<{ source: string; target: string }>;
                                          critical_path_ids?: string[] } | undefined);
  const criticalSet = useMemo(
    () => new Set(depData?.critical_path_ids ?? []),
    [depData?.critical_path_ids],
  );
  const edges = depData?.edges ?? [];
  const predecessors = useMemo(() => {
    const map = new Map<string, string[]>();
    edges.forEach((e) => {
      if (!map.has(e.target)) map.set(e.target, []);
      map.get(e.target)!.push(e.source);
    });
    return map;
  }, [edges]);
  const successors = useMemo(() => {
    const map = new Map<string, string[]>();
    edges.forEach((e) => {
      if (!map.has(e.source)) map.set(e.source, []);
      map.get(e.source)!.push(e.target);
    });
    return map;
  }, [edges]);

  // Build phase-grouped tree, honouring the current text filter.
  const phaseGroups = useMemo(() => {
    const lc = filter.trim().toLowerCase();
    const byPhase = new Map<string, TaskRow[]>();
    tasks.forEach((t) => {
      if (lc) {
        const hit = String(t.id).toLowerCase().includes(lc)
                 || String(t.name).toLowerCase().includes(lc);
        if (!hit) return;
      }
      const key = t.phase ?? "(unassigned)";
      if (!byPhase.has(key)) byPhase.set(key, []);
      byPhase.get(key)!.push(t);
    });
    // Preserve canonical phase ordering, append unknowns at the end.
    const ordered: Array<{ id: string; code: string | null; name: string; rows: TaskRow[] }> = [];
    phases.forEach((p) => {
      const rows = byPhase.get(p.id);
      if (rows && rows.length) ordered.push({ id: p.id, code: p.code, name: p.name, rows });
      byPhase.delete(p.id);
    });
    byPhase.forEach((rows, id) => ordered.push({ id, code: null, name: id, rows }));
    return ordered;
  }, [tasks, phases, filter]);

  const selected = selectedId ? tasks.find((t) => t.id === selectedId) ?? null : null;
  const selectedPredecessors: DependencyNode[] = selected
    ? (predecessors.get(selected.id) ?? []).map((id) => ({
        id, label: tasks.find((t) => t.id === id)?.name ?? id,
        is_critical: criticalSet.has(id),
      }))
    : [];
  const selectedSuccessors: DependencyNode[] = selected
    ? (successors.get(selected.id) ?? []).map((id) => ({
        id, label: tasks.find((t) => t.id === id)?.name ?? id,
        is_critical: criticalSet.has(id),
      }))
    : [];

  useRightPane(
    useMemo(() => (
      <ImpactPane selectedId={selectedId} selected={selected}
                  successors={selectedSuccessors} openChat={openChat} />
    ), [selectedId, selected, selectedSuccessors, openChat]),
  );

  const togglePhase = (phaseId: string) => {
    setExpanded((curr) => {
      const next = new Set(curr);
      if (next.has(phaseId)) next.delete(phaseId);
      else next.add(phaseId);
      return next;
    });
  };

  // Auto-expand the phase that owns the selected task so the tree shows it.
  const effectiveExpanded = useMemo(() => {
    if (!selected) return expanded;
    const out = new Set(expanded);
    out.add(selected.phase);
    return out;
  }, [expanded, selected]);

  return (
    <div className="grid h-full min-h-0 grid-cols-1 gap-3 overflow-hidden lg:grid-cols-[280px_minmax(0,1fr)]">
      {/* Left: phase tree + filter */}
      <aside className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div className="flex flex-shrink-0 items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs">
          <div className="font-medium text-slate-700">All work-packages</div>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="filter…"
            className="w-32 rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] placeholder:text-slate-400"
          />
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-2 text-xs">
          {tasksTool.loading ? <Skeleton lines={6} /> :
           tasksTool.error ? <ErrorState error={tasksTool.error} onRetry={tasksTool.refetch} /> :
           phaseGroups.length === 0 ? (
             <div className="p-4 text-slate-500">No tasks match the filter.</div>
           ) : phaseGroups.map((g) => (
             <div key={g.id} className="mb-1">
               <button
                 type="button"
                 onClick={() => togglePhase(g.id)}
                 className="block w-full rounded px-1 py-0.5 text-left font-medium text-slate-800 hover:bg-slate-100"
               >
                 {effectiveExpanded.has(g.id) ? "▾" : "▸"}{" "}
                 {g.code ? `${g.code} ` : ""}{g.name}{" "}
                 <span className="text-[10px] text-slate-400">({g.rows.length})</span>
               </button>
               {effectiveExpanded.has(g.id) && (
                 <ul className="ml-3 mt-0.5 space-y-0.5 border-l border-slate-200 pl-2">
                   {g.rows.map((t) => {
                     const active = t.id === selectedId;
                     const isCritical = criticalSet.has(t.id);
                     return (
                       <li key={t.id}>
                         <button
                           type="button"
                           onClick={() => setSelectedId(t.id)}
                           className={
                             "block w-full truncate rounded px-1 py-0.5 text-left text-[11px] " +
                             (active
                               ? "bg-slate-900 text-white"
                               : isCritical
                                 ? "text-rose-700 hover:bg-rose-50"
                                 : "text-slate-700 hover:bg-slate-100")
                           }
                           title={`${t.id} · ${t.name}`}
                         >
                           <span className="font-mono">{t.id}</span>{isCritical ? " ✦" : ""} · {t.name}
                         </button>
                       </li>
                     );
                   })}
                 </ul>
               )}
             </div>
           ))}
        </div>
      </aside>

      {/* Middle: detail */}
      <main className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white">
        {selected == null ? (
          <div className="flex h-full items-center justify-center p-12 text-sm text-slate-500">
            Click a task on the left to view its property grid + dependencies.
          </div>
        ) : (
          <Detail task={selected}
                  isCritical={criticalSet.has(selected.id)}
                  predecessors={selectedPredecessors}
                  successors={selectedSuccessors}
                  onPickTask={setSelectedId} />
        )}
      </main>
    </div>
  );
}

function Detail({ task, isCritical, predecessors, successors, onPickTask }: {
  task: TaskRow;
  isCritical: boolean;
  predecessors: DependencyNode[];
  successors: DependencyNode[];
  onPickTask: (id: string) => void;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col overflow-auto p-4">
      <header className="mb-3">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded bg-slate-900 px-2 py-0.5 font-mono text-white">{task.id}</span>
          {isCritical && (
            <span className="rounded bg-rose-100 px-2 py-0.5 font-medium text-rose-700">critical</span>
          )}
          {task.phase && (
            <span className="rounded bg-sky-100 px-2 py-0.5 text-sky-700">phase {task.phase}</span>
          )}
        </div>
        <h2 className="mt-2 text-lg font-semibold text-slate-900">{task.name}</h2>
      </header>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <section className="rounded border border-slate-200 p-3">
          <div className="mb-2 text-sm font-medium text-slate-700">Properties</div>
          <dl className="grid grid-cols-[110px_minmax(0,1fr)] gap-y-1 text-xs">
            <dt className="text-slate-500">phase</dt><dd>{task.phase ?? "—"}</dd>
            <dt className="text-slate-500">duration</dt>
            <dd>{task.duration_days == null ? "—" : `${task.duration_days} d`}</dd>
            <dt className="text-slate-500">resources</dt>
            <dd>{task.resources?.length ? task.resources.join(", ") : "—"}</dd>
            <dt className="text-slate-500">on critical</dt>
            <dd className={isCritical ? "text-rose-600" : ""}>{isCritical ? "yes" : "no"}</dd>
          </dl>
        </section>

        <section className="rounded border border-slate-200 p-3">
          <div className="mb-2 text-sm font-medium text-slate-700">Dependencies</div>
          <div className="space-y-2 text-xs">
            <div>
              <div className="font-medium text-slate-600">Predecessors ({predecessors.length})</div>
              {predecessors.length === 0 ? (
                <div className="text-slate-400">No predecessors.</div>
              ) : (
                <ul className="ml-3 list-disc space-y-0.5">
                  {predecessors.map((d) => (
                    <li key={d.id}>
                      <button type="button" onClick={() => onPickTask(d.id)}
                              className={d.is_critical ? "text-rose-700 hover:underline" : "text-sky-700 hover:underline"}>
                        {d.id} · {d.label}{d.is_critical ? " ✦" : ""}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <div className="font-medium text-slate-600">Successors ({successors.length})</div>
              {successors.length === 0 ? (
                <div className="text-slate-400">No successors (sink task).</div>
              ) : (
                <ul className="ml-3 list-disc space-y-0.5">
                  {successors.map((d) => (
                    <li key={d.id}>
                      <button type="button" onClick={() => onPickTask(d.id)}
                              className={d.is_critical ? "text-rose-700 hover:underline" : "text-sky-700 hover:underline"}>
                        {d.id} · {d.label}{d.is_critical ? " ✦" : ""}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function ImpactPane({ selectedId, selected, successors, openChat }: {
  selectedId: string | null;
  selected: TaskRow | null;
  successors: DependencyNode[];
  openChat: (prefill?: string) => void;
}) {
  if (!selectedId || !selected) {
    return (
      <div className="rounded border border-dashed border-slate-200 p-3 text-xs text-slate-500">
        Select a task to see the impact of moving its end date.
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <section>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Impact of moving this task</div>
        <div className="mt-2 rounded border border-slate-200 p-2 text-xs">
          <div className="font-medium">{selected.id} · {selected.name}</div>
          <div className="mt-2 text-slate-500">
            Push end +N days — opens a what-if simulation in chat that re-runs the
            critical path and reports the new project end date.
          </div>
          <div className="mt-2 grid grid-cols-4 gap-1">
            {[-5, 0, 10, 20].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => openChat(`Run a what-if: change ${selectedId} duration by ${n} days. Show the new project end date, the new critical-path diff, and any downstream tasks affected.`)}
                className="rounded border border-slate-200 bg-white px-2 py-1 hover:bg-slate-100"
              >{n > 0 ? `+${n}` : n}</button>
            ))}
          </div>
          <div className="mt-3 text-[11px]">
            <div className="text-slate-500">downstream affected</div>
            <div className="font-mono">{successors.length} task{successors.length === 1 ? "" : "s"}</div>
          </div>
        </div>
      </section>
    </div>
  );
}
